import type { Emitter } from "@core/events/emitter";

/**
 * `IEventModel` — web port of Unity's
 * `Models/EventModel/IServerEventListenerModel.cs` (via `ISessionModel`).
 *
 * Unity's listener exposes ~50 typed C# events; the web port collapses
 * those onto a single envelope `{ type, payload }` so consumers can
 * subscribe with a discriminated union and we don't fan out the
 * interface every time a new event type lands. Typed payload helpers
 * sit in `eventTypes.ts` (added in M6 alongside the WebSocket impl).
 *
 * Transport (Unity reference: `Infrastructure/Networking/AWSAppsyncPubsub.cs`
 * + `Models/EventModel/SessionModel.cs`):
 *   - Connect: open a `WebSocket` to the session host using the current
 *     ID token from `IAuthModel`.
 *   - Frame envelope: `{ route, payload, tc, sn }` JSON.
 *   - Reconnect with backoff on close; emit `onReconnecting` /
 *     `onReconnected` so the UI can show the no-network banner.
 *
 * M5 lands the **interface only** + a no-op `OfflineEventModel`. The
 * real `WebSocketEventModel` lands in M6 wired into
 * `ProjectModel.applyEntitySnapshotChanges` etc.
 */

export interface ServerEvent {
  /** Wire-level event-type string from Unity's `KnownEventTypes`. */
  readonly type: string;
  readonly payload: unknown;
  /** Server-assigned event UID; carry through for de-dup. */
  readonly eventUid?: string;
  /** Client send timestamp in Unix milliseconds. */
  readonly senderTimestampMillis?: number;
}

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface IEventModel {
  readonly state: ConnectionState;

  readonly onEvent: Emitter<ServerEvent>;
  readonly onStateChanged: Emitter<ConnectionState>;

  connectAsync(signal?: AbortSignal): Promise<void>;
  disconnect(): void;

  /** Send a fire-and-forget message to the session host (qos = 0). */
  send(envelope: ServerEvent): void;
}

export const EVENT_MODEL = Symbol.for("Campfire.EventModel");
